import React, { useRef, useState, useEffect, useCallback } from 'react'

import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }

// Đo suất điện động & điện trở trong của nguồn (Lớp 11 — Bài 17):
// U = E − I·r. Đồ thị thực nghiệm U theo I là đường thẳng: giao trục U = E, độ dốc = −r.
const W = 620, H = 300
const NOISE = [0.03, -0.05, 0.02, -0.02, 0.04, -0.03, 0.01, -0.04, 0.03]   // nhiễu thực nghiệm cố định

const EMFMeasurementSimulation: React.FC<Props> = ({
  title = 'Đo suất điện động & điện trở trong (đồ thị I–U)',
  description = 'Mạch đo: U = E − I·r. Đồ thị U theo I là đường thẳng — giao với trục U cho suất điện động E, độ dốc cho điện trở trong r.',
  onComplete,
}) => {
  const cv = useRef<HTMLCanvasElement>(null)
  const [E, setE] = useState(4.5), [r, setRr] = useState(1.2)
  const firstRef = useRef(false)
  const touch = () => { if (!firstRef.current) { firstRef.current = true; onComplete?.() } }

  const draw = useCallback(() => {
    const ctx = prepCanvas(cv.current, W, H); if (!ctx) return
    ctx.clearRect(0, 0, W, H)
    const x0 = 56, y0 = H - 44, x1 = W - 18, yTop = 22
    const Imax = E / r, xmax = Math.ceil(Imax * 1.05 * 10) / 10, ymax = Math.ceil(E * 1.15)
    const X = (i: number) => x0 + (i / xmax) * (x1 - x0)
    const Y = (u: number) => y0 - (u / ymax) * (y0 - yTop)
    // lưới + nhãn số
    ctx.font = '10px sans-serif'; ctx.strokeStyle = '#eef2f7'; ctx.lineWidth = 1; ctx.fillStyle = '#94a3b8'
    ctx.textAlign = 'right'
    for (let u = 1; u <= ymax; u++) { ctx.beginPath(); ctx.moveTo(x0, Y(u)); ctx.lineTo(x1, Y(u)); ctx.stroke(); ctx.fillText(String(u), x0 - 5, Y(u) + 3) }
    ctx.textAlign = 'center'
    for (let i = 0.5; i <= xmax; i += 0.5) { ctx.beginPath(); ctx.moveTo(X(i), yTop); ctx.lineTo(X(i), y0); ctx.stroke(); ctx.fillText(i.toFixed(1), X(i), y0 + 14) }
    ctx.textAlign = 'left'
    // trục + mũi tên
    ctx.strokeStyle = '#1e293b'; ctx.fillStyle = '#1e293b'; ctx.lineWidth = 2; ctx.lineJoin = 'round'
    ctx.beginPath(); ctx.moveTo(x0, yTop - 2); ctx.lineTo(x0, y0); ctx.lineTo(x1, y0); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(x0, yTop - 8); ctx.lineTo(x0 - 4, yTop); ctx.lineTo(x0 + 4, yTop); ctx.closePath(); ctx.fill()
    ctx.beginPath(); ctx.moveTo(x1 + 6, y0); ctx.lineTo(x1 - 2, y0 - 4); ctx.lineTo(x1 - 2, y0 + 4); ctx.closePath(); ctx.fill()
    ctx.font = 'bold 12px sans-serif'; ctx.fillText('U (V)', x0 + 4, 14); ctx.fillText('I (A)', x1 - 30, y0 + 24)
    // đường thẳng lý thuyết U = E − I·r
    ctx.strokeStyle = '#2563eb'; ctx.lineWidth = 2.6; ctx.beginPath(); ctx.moveTo(X(0), Y(E)); ctx.lineTo(X(Imax), Y(0)); ctx.stroke()
    // điểm thực nghiệm
    ctx.fillStyle = '#dc2626'
    for (let k = 0; k < 9; k++) { const I = (k / 9) * Imax * 0.95; const U = E - I * r + NOISE[k] * E; ctx.beginPath(); ctx.arc(X(I), Y(U), 4, 0, Math.PI * 2); ctx.fill() }
    // giao trục U = E
    ctx.fillStyle = '#16a34a'; ctx.beginPath(); ctx.arc(X(0), Y(E), 5, 0, Math.PI * 2); ctx.fill()
    ctx.strokeStyle = 'rgba(22,163,74,0.5)'; ctx.setLineDash([4, 3]); ctx.beginPath(); ctx.moveTo(x0, Y(E)); ctx.lineTo(X(Imax * 0.3), Y(E)); ctx.stroke(); ctx.setLineDash([])
    // chú giải
    ctx.fillStyle = '#0f172a'; ctx.font = 'bold 12px sans-serif'
    ctx.fillText(`E = ${E.toFixed(1)} V (giao trục U)`, X(Imax * 0.32), Y(E) - 6)
    ctx.fillStyle = '#334155'; ctx.fillText(`U = E − I·r   →   r = ${r.toFixed(1)} Ω (độ dốc)`, x0 + 8, Y(E * 0.45))
  }, [E, r])
  useEffect(() => { draw() }, [draw])

  return (
    <div className="rounded-2xl border border-amber-200 bg-white p-4 my-3">
      <h4 className="font-bold text-amber-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <canvas ref={cv} width={W} height={H} className="w-full rounded-lg border border-gray-100 bg-slate-50" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 mt-3 text-sm">
        <label className="flex items-center gap-2"><span className="w-36 text-gray-600">Suất điện động E</span><input type="range" min={1.5} max={9} step={0.5} value={E} onChange={(e) => { touch(); setE(Number(e.target.value)) }} className="flex-1 accent-amber-500" /><span className="w-12 text-right font-semibold">{E} V</span></label>
        <label className="flex items-center gap-2"><span className="w-36 text-gray-600">Điện trở trong r</span><input type="range" min={0.2} max={3} step={0.1} value={r} onChange={(e) => { touch(); setRr(Number(e.target.value)) }} className="flex-1 accent-amber-500" /><span className="w-12 text-right font-semibold">{r} Ω</span></label>
      </div>
      <p className="text-[11px] text-gray-400 mt-2">Đo U ứng với các giá trị I khác nhau (điểm đỏ) → vẽ đường thẳng U = E − I·r. Tung độ gốc (I = 0) bằng suất điện động E; độ dốc đường thẳng bằng −r → suy ra điện trở trong.</p>
    </div>
  )
}
export default EMFMeasurementSimulation
